import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apiFetch, useAuth } from '../App';
import { ga } from '../utils/ga4';
import { sharePage } from '../utils/share';
import ParallaxBackground from '../components/ParallaxBackground';
import { MapPin, Clock, Phone, ArrowRight, Star, ChevronDown, ChevronUp, Navigation, Scissors, Droplets, Sparkles, Check, Plus, Share2 } from 'lucide-react';

function formatPrice(p) {
  return p === 0 ? 'Varies' : `₹${p.toLocaleString('en-IN')}`;
}

function priceRange(variants) {
  const prices = variants.map(v => v.price).filter(p => p > 0);
  if (!prices.length) return 'Price varies';
  const mn = Math.min(...prices);
  const mx = Math.max(...prices);
  return mn === mx ? formatPrice(mn) : `${formatPrice(mn)} – ${formatPrice(mx)}`;
}

const pkgIcons = { 'Puppy': Sparkles, 'Basic': Droplets, 'Premium': Sparkles, 'Medicated': Droplets, 'Massage': Sparkles, 'Soothe': Sparkles, 'Signature': Scissors, 'Derma': Droplets, 'Essential': Droplets, 'Fresh': Sparkles, 'Hair Cut': Scissors, 'Kitten': Sparkles, 'Meow': Sparkles, 'Coat': Droplets, 'Prestige': Sparkles };
function getPkgIcon(name) {
  for (const [k, v] of Object.entries(pkgIcons)) { if (name.includes(k)) return v; }
  return Scissors;
}

function PackageCard({ pkg, nodeSlug, navigate }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = getPkgIcon(pkg.name);
  const included = pkg.description ? pkg.description.split(' | ') : [];
  const hasSizes = pkg.variants.length > 1 || (pkg.variants.length === 1 && pkg.variants[0].size !== 'ALL');

  return (
    <div data-testid={`catalog-pkg-${pkg.id}`} className="petfolk-card flex flex-col transition-all duration-300">
      <div className="p-5 sm:p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-petfolk-orange/15 to-petfolk-purple/10 flex items-center justify-center shrink-0">
            <Icon size={20} className="text-petfolk-orange" />
          </div>
          <span className="font-outfit text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {priceRange(pkg.variants)}
          </span>
        </div>
        <h3 className="font-outfit font-bold text-lg mb-1" style={{ color: 'var(--text-primary)' }}>{pkg.name}</h3>
        {pkg.notes && <span className="text-xs px-2.5 py-0.5 rounded-full font-medium mb-2 inline-block w-fit" style={{ background: 'rgba(255,140,66,0.12)', color: '#c2410c' }}>{pkg.notes}</span>}

        {/* Size variants as chips */}
        {hasSizes && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {pkg.variants.map(v => (
              <span key={v.size} className="text-xs px-2.5 py-1 rounded-lg font-medium" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
                {v.size === 'EXTRA_LARGE' ? 'XL' : v.size} — {formatPrice(v.price)}
              </span>
            ))}
          </div>
        )}

        {/* Services included preview */}
        <div className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
          {expanded ? (
            <ul className="space-y-1 animate-fade-in">
              {included.map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check size={13} className="text-green-500 shrink-0" />
                  <span>{item.trim()}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="line-clamp-2">{included.slice(0, 3).join(' · ')}{included.length > 3 ? ` +${included.length - 3} more` : ''}</p>
          )}
        </div>

        <div className="flex items-center justify-between mt-auto pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`expand-pkg-${pkg.id}`}
            className="text-xs font-semibold flex items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: 'var(--petfolk-purple)' }}
          >
            {expanded ? <><ChevronUp size={12} /> Less</> : <><ChevronDown size={12} /> Details</>}
          </button>
          <button
            data-testid={`book-pkg-${pkg.id}`}
            onClick={() => navigate(`/book/${nodeSlug}?catalog=${pkg.id}`)}
            className="btn-primary text-sm py-2 px-4"
          >
            Book <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function AlaCarteSection({ items, species }) {
  const [showAll, setShowAll] = useState(false);
  const filtered = items.filter(i => i.species === species);
  if (!filtered.length) return null;
  const visible = showAll ? filtered : filtered.slice(0, 8);

  return (
    <div className="mt-8" data-testid={`ala-carte-section-${species}`}>
      <h3 className="font-outfit text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
        <Plus size={18} className="text-petfolk-purple" />
        A-la-carte Add-ons
        <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>
          Can be added to any package
        </span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {visible.map(item => {
          const hasSizes = item.variants.length > 1 || (item.variants.length === 1 && item.variants[0].size !== 'ALL');
          return (
            <div key={item.id} data-testid={`ala-carte-${item.id}`}
              className="petfolk-card p-3 sm:p-4 flex flex-col"
            >
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{item.name}</p>
              {item.notes && <span className="text-xs mb-1" style={{ color: '#c2410c' }}>{item.notes}</span>}
              {hasSizes ? (
                <div className="space-y-0.5 mt-auto">
                  {item.variants.map(v => (
                    <div key={v.size} className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text-secondary)' }}>{v.size === 'EXTRA_LARGE' ? 'XL' : v.size}</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{formatPrice(v.price)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="font-outfit font-bold text-base mt-auto" style={{ color: 'var(--text-primary)' }}>
                  {formatPrice(item.variants[0]?.price)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {filtered.length > 8 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 text-sm font-semibold flex items-center gap-1 mx-auto"
          style={{ color: 'var(--petfolk-purple)' }}
          data-testid="show-all-ala-carte"
        >
          {showAll ? 'Show Less' : `Show All ${filtered.length} Add-ons`}
          {showAll ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
    </div>
  );
}

export default function StorePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, setShowAuth } = useAuth();
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [speciesFilter, setSpeciesFilter] = useState(localStorage.getItem('petfolk_species') || '');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const reserved = ['support', 'paws', 'den', 'book', 'franchise'];
    if (reserved.includes(slug)) return;
    setLoading(true);
    apiFetch(`/api/nodes/${slug}`)
      .then(data => {
        setNode(data);
        setLoading(false);
        // GA4: track which store the user is viewing
        ga.storeView(data.slug, data.displayName, data.cityName);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  // Inject JSON-LD structured data + canonical link directly to <head>
  // (react-helmet-async v2 strips inline <script> and some <link> tags)
  useEffect(() => {
    if (!node) return;
    const canonicalUrl = `https://petfolk.in/${node.slug}`;
    const storeDesc = `Book pet grooming at ${node.displayName}, ${node.address}. Premium services, certified groomers & live streaming. Call ${node.phone}`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": node.displayName,
      "image": node.heroImageUrl,
      "url": canonicalUrl,
      "telephone": node.phone,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": node.address,
        "addressLocality": node.cityName,
        "addressRegion": node.cityName === "Mumbai" ? "Maharashtra" : "Telangana",
        "addressCountry": "IN"
      },
      "geo": { "@type": "GeoCoordinates", "latitude": node.lat, "longitude": node.lng },
      "openingHours": node.operatingHours?.display,
      "priceRange": "₹400 - ₹3000",
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "120" },
      "hasMap": node.mapUrl,
      "description": storeDesc,
      "areaServed": node.cityName,
      "serviceType": "Pet Grooming"
    };

    // Inject JSON-LD
    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'petfolk-store-jsonld';
    ldScript.text = JSON.stringify(jsonLd);
    document.querySelectorAll('#petfolk-store-jsonld').forEach(el => el.remove());
    document.head.appendChild(ldScript);

    // Inject canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    return () => {
      document.querySelectorAll('#petfolk-store-jsonld').forEach(el => el.remove());
      const cl = document.querySelector('link[rel="canonical"]');
      if (cl) cl.remove();
    };
  }, [node]);

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center pt-20" style={{ background: 'var(--bg-primary)' }}>
      <div className="text-center">
        <h1 className="font-outfit text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Store Not Found</h1>
        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>We couldn't find a Petfolk location at this address.</p>
        <Link to="/" className="btn-primary">Back to Home</Link>
      </div>
    </div>
  );

  if (loading || !node) return (
    <div className="min-h-screen flex items-center justify-center pt-20" style={{ background: 'var(--bg-primary)' }}>
      <div className="w-12 h-12 border-4 border-petfolk-orange border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const catalog = node.catalog || [];
  const hasCatalog = catalog.length > 0;
  const packages = catalog.filter(c => c.type === 'PACKAGE');
  const alaCarte = catalog.filter(c => c.type === 'ALA_CARTE');

  // Determine species availability
  const dogPkgs = packages.filter(p => p.species === 'DOG');
  const catPkgs = packages.filter(p => p.species === 'CAT');
  const hasDog = dogPkgs.length > 0;
  const hasCat = catPkgs.length > 0;

  // Default species filter based on what's available
  const activeSpecies = speciesFilter || (hasDog ? 'DOG' : 'CAT');
  const filteredPkgs = packages.filter(p => p.species === activeSpecies);

  // Legacy services (for stores without catalog, e.g. Bandra)
  const legacyServices = (node.services || []).filter(s => !speciesFilter || s.species === speciesFilter);

  const storeTitle = `${node.displayName} — Pet Grooming in ${node.cityName} | Petfolk`;
  const storeDesc = `Book pet grooming at ${node.displayName}, ${node.address}. Premium services, certified groomers & live streaming. Call ${node.phone}`;
  const canonicalUrl = `https://petfolk.in/${node.slug}`;

  return (
    <div className="relative min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Helmet>
        <title>{storeTitle}</title>
        <meta name="description" content={storeDesc} />
        <meta name="keywords" content={`pet grooming ${node.slug.replace(/-/g, ' ')}, dog grooming ${node.cityName}, cat grooming ${node.cityName}, ${node.displayName}, pet salon ${node.slug.replace(/-/g, ' ')}`} />
        <meta property="og:title" content={storeTitle} />
        <meta property="og:description" content={storeDesc} />
        <meta property="og:image" content={node.heroImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="business.business" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={storeTitle} />
        <meta name="twitter:description" content={storeDesc} />
        <meta name="twitter:image" content={node.heroImageUrl} />
      </Helmet>
      <ParallaxBackground />

      {/* Store Hero */}
      <section data-testid="store-hero" className="relative pt-16">
        <div className="relative h-[300px] sm:h-[400px] overflow-hidden">
          <img src={node.heroImageUrl} alt={node.displayName} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-7xl mx-auto">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-white/70 text-sm font-medium uppercase tracking-wider">{node.type} {node.isFranchise && '· Franchise'}</span>
                <h1 className="font-outfit text-3xl sm:text-5xl font-extrabold text-white mt-1">{node.displayName}</h1>
                <p className="text-white/80 mt-2 flex items-center gap-2">
                  <MapPin size={16} /> {node.localName} - {node.cityName}
                </p>
              </div>
              <div className="hidden sm:flex gap-3">
                <a href={`https://wa.me/${(node.phone||'').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="rounded-2xl px-5 py-3 flex items-center gap-2 text-white text-sm font-semibold hover:scale-105 transition-transform" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(12px)' }}>
                  <Phone size={16} /> Call Us
                </a>
                <a href={node.mapUrl || `https://maps.google.com/?q=${node.lat},${node.lng}`} target="_blank" rel="noreferrer" className="rounded-2xl px-5 py-3 flex items-center gap-2 text-white text-sm font-semibold hover:scale-105 transition-transform" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(12px)' }}>
                  <Navigation size={16} /> Directions
                </a>
                <button
                  onClick={async () => {
                    const m = await sharePage({
                      title: `${node.displayName} · ${node.cityName}`,
                      text: `Pet grooming at ${node.displayName} — ${node.address}`,
                      type: 'store',
                      slug: node.slug,
                    });
                    if (m === 'clipboard') alert('Link copied!');
                  }}
                  data-testid="store-share-btn"
                  className="rounded-2xl px-5 py-3 flex items-center gap-2 text-white text-sm font-semibold hover:scale-105 transition-transform"
                  style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(12px)' }}
                >
                  <Share2 size={16} /> Share
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Store Info Bar */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 -mt-6">
        <div className="petfolk-card p-4 sm:p-6 flex flex-wrap items-center gap-4 sm:gap-8">
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <Clock size={16} className="text-petfolk-orange" />
            <span>{node.operatingHours?.display || `${node.operatingHours?.open} – ${node.operatingHours?.close}`}</span>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <MapPin size={16} className="text-petfolk-orange" />
            <a href={node.mapUrl || `https://maps.google.com/?q=${node.lat},${node.lng}`} target="_blank" rel="noreferrer" className="hover:text-petfolk-orange transition-colors">{node.address}</a>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <Phone size={16} className="text-petfolk-orange" />
            <a href={`tel:${node.phone}`} className="hover:text-petfolk-orange transition-colors">{node.phone}</a>
          </div>
          <div className="flex items-center gap-1 text-sm ml-auto">
            <Star size={16} className="text-yellow-500 fill-yellow-500" />
            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>4.9</span>
          </div>
        </div>
      </section>

      {/* Services / Catalog */}
      <section data-testid="services-catalog" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {hasCatalog ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="font-outfit text-2xl sm:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Grooming Menu
              </h2>
              <div className="flex gap-2">
                {hasDog && (
                  <button
                    data-testid="filter-DOG"
                    onClick={() => setSpeciesFilter('DOG')}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeSpecies === 'DOG' ? 'bg-petfolk-orange text-white' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                    style={{ color: activeSpecies !== 'DOG' ? 'var(--text-secondary)' : undefined }}
                  >
                    Dogs
                  </button>
                )}
                {hasCat && (
                  <button
                    data-testid="filter-CAT"
                    onClick={() => setSpeciesFilter('CAT')}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeSpecies === 'CAT' ? 'bg-petfolk-orange text-white' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                    style={{ color: activeSpecies !== 'CAT' ? 'var(--text-secondary)' : undefined }}
                  >
                    Cats
                  </button>
                )}
              </div>
            </div>

            {/* Packages */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredPkgs.map(pkg => (
                <PackageCard key={pkg.id} pkg={pkg} nodeSlug={node.slug} navigate={navigate} />
              ))}
            </div>

            {/* A-la-carte */}
            <AlaCarteSection items={alaCarte} species={activeSpecies} />
          </>
        ) : (
          /* Legacy services for stores without catalog (Bandra) */
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="font-outfit text-2xl sm:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Our Services</h2>
              <div className="flex gap-2">
                {['', 'DOG', 'CAT'].map(s => (
                  <button key={s} data-testid={`filter-${s || 'all'}`} onClick={() => setSpeciesFilter(s)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${speciesFilter === s ? 'bg-petfolk-orange text-white' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                    style={{ color: speciesFilter !== s ? 'var(--text-secondary)' : undefined }}>
                    {s === '' ? 'All' : s === 'DOG' ? 'Dogs' : 'Cats'}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {legacyServices.map(svc => (
                <div key={svc.id} data-testid={`service-card-${svc.id}`} className="petfolk-card p-6 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-petfolk-orange/15 to-petfolk-purple/10 flex items-center justify-center shrink-0">
                      <Scissors size={22} className="text-petfolk-orange" />
                    </div>
                    <span className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      ₹{(svc.effectivePrice || svc.basePrice).toLocaleString()}
                    </span>
                  </div>
                  <h3 className="font-outfit font-bold text-lg mb-1" style={{ color: 'var(--text-primary)' }}>{svc.name}</h3>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>{svc.description}</p>
                  <div className="flex items-center justify-between mt-auto pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                      {svc.baseDurationMinutes} min
                    </span>
                    <button data-testid={`book-service-${svc.id}`} onClick={() => navigate(`/book/${node.slug}?service=${svc.id}`)} className="btn-primary text-sm py-2 px-4">
                      Book <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Book Now CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-3xl p-8 text-center text-white" style={{ background: `linear-gradient(135deg, ${node.themePrimaryColor || '#FF8C42'} 0%, ${node.themeSecondaryColor || '#8B5CF6'} 100%)` }}>
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold mb-3">Ready to book at {node.displayName}?</h2>
          <p className="opacity-90 mb-6">Pay after your service - no advance payment needed!</p>
          <button
            data-testid="store-cta-book"
            onClick={() => navigate(`/book/${node.slug}`)}
            className="bg-white font-outfit font-bold rounded-2xl px-8 py-4 hover:scale-105 transition-transform"
            style={{ color: node.themePrimaryColor || '#FF8C42' }}
          >
            Book Now <ArrowRight size={18} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
}
